import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, DocumentTextIcon, QrCodeIcon, UserPlusIcon } from '@heroicons/react/24/outline';

const OrderSuccessPage = () => {
    const { t } = useTranslation();
    const { lang } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Get order data from navigation state
    const { orderId, orderData } = location.state || {};

    const handleViewAllOrders = () => {
        navigate(`/${lang}/my-orders`);
    };

    const handleManageQRProfiles = () => {
        navigate(`/${lang}/my-profile`);
    };

    const handleCreateProfile = () => {
        navigate(`/${lang}/create-profile`);
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12" data-testid="order-success-page">
            <div className="container mx-auto px-4">
                <div className="max-w-2xl mx-auto">
                    {/* Success Header */}
                    <div className="text-center mb-8">
                        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
                            <CheckCircleIcon className="h-12 w-12 text-green-600" />
                        </div>
                        <h1 className="text-4xl font-bold text-gray-900 mb-6" data-testid="order-success-title">
                            {t('orderSuccessPage.title')}
                        </h1>
                        
                        {/* Beautiful Success Message Frame */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 shadow-lg mb-4">
                            <p className="text-lg font-semibold text-green-800 leading-relaxed" data-testid="order-success-message">
                                {t('orderSuccessPage.message')}
                            </p>
                            <div className="mt-4 flex justify-center">
                                <div className="w-16 h-1 bg-green-400 rounded-full"></div>
                            </div>
                        </div>
                    </div>

                    {/* Order Details Card */}
                    {orderId && (
                        <div className="bg-white rounded-lg shadow-lg p-6 mb-8" data-testid="order-success-details">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                {t('orderSuccessPage.orderDetailsTitle')}
                            </h2>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">{t('orderSuccessPage.orderIdLabel')}:</span>
                                    <span className="font-semibold text-gray-900" data-testid="order-success-order-id">
                                        #{orderId}
                                    </span>
                                </div>
                                {orderData && (
                                    <>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600">{t('orderSuccessPage.totalAmountLabel')}:</span>
                                            <span className="font-semibold text-gray-900" data-testid="order-success-total">
                                                {orderData.totalAmount?.toFixed(2)} {t('currency.mkd')}
                                            </span>
                                        </div>
                                        {orderData.productSize && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">{t('checkoutPage.sizeLabel')}:</span>
                                                <span className="font-semibold text-gray-900" data-testid="order-success-size">
                                                    {orderData.productSize} ({orderData.productSize === 'S' ? '6x6 cm' : orderData.productSize === 'M' ? '8x8 cm' : '10x10 cm'})
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600">{t('orderSuccessPage.statusLabel')}:</span>
                                            <span className="px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-700" data-testid="order-success-status">
                                                {t(`orderStatus.${orderData.status}`, orderData.status)}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Next Steps */}
                    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">
                            {t('orderSuccessPage.nextStepsTitle')}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {t('orderSuccessPage.nextStepsMessage')}
                        </p>
                        
                        {/* Action Buttons */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* View All Orders */}
                            <button
                                onClick={handleViewAllOrders}
                                className="flex flex-col items-center p-6 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors duration-200 group"
                                data-testid="order-success-view-orders-btn"
                            >
                                <DocumentTextIcon className="h-8 w-8 text-indigo-600 mb-3 group-hover:scale-110 transition-transform duration-200" />
                                <h3 className="text-lg font-semibold text-indigo-900 mb-2">
                                    {t('orderSuccessPage.viewAllOrdersTitle')}
                                </h3>
                                <p className="text-sm text-indigo-700 text-center">
                                    {t('orderSuccessPage.viewAllOrdersDescription')}
                                </p>
                            </button>

                            {/* Manage QR Profiles */}
                            <button
                                onClick={handleManageQRProfiles}
                                className="flex flex-col items-center p-6 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors duration-200 group"
                                data-testid="order-success-manage-profiles-btn"
                            >
                                <QrCodeIcon className="h-8 w-8 text-purple-600 mb-3 group-hover:scale-110 transition-transform duration-200" />
                                <h3 className="text-lg font-semibold text-purple-900 mb-2">
                                    {t('orderSuccessPage.manageQRProfilesTitle')}
                                </h3>
                                <p className="text-sm text-purple-700 text-center">
                                    {t('orderSuccessPage.manageQRProfilesDescription')}
                                </p>
                            </button>

                            {/* Create Profile */}
                            <button
                                onClick={handleCreateProfile}
                                className="flex flex-col items-center p-6 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors duration-200 group"
                                data-testid="order-success-create-profile-btn"
                            >
                                <UserPlusIcon className="h-8 w-8 text-green-600 mb-3 group-hover:scale-110 transition-transform duration-200" />
                                <h3 className="text-lg font-semibold text-green-900 mb-2">
                                    {t('orderSuccessPage.createProfileTitle')}
                                </h3>
                                <p className="text-sm text-green-700 text-center">
                                    {t('orderSuccessPage.createProfileDescription')}
                                </p>
                            </button>
                        </div>
                    </div>


                </div>
            </div>
        </div>
    );
};

export default OrderSuccessPage; 